import type { FC, MouseEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { PagePaths } from "@/partner/shared/constants";
import { formatAmountObject } from "@/partner/shared/utils/formatCurrency";
import type {
  UnpaidInvoiceLike,
  UnpaidInvoicesSummary,
} from "../model/unpaidInvoices";

interface UnpaidInvoicesCardProps {
  summary: UnpaidInvoicesSummary<UnpaidInvoiceLike>;
}

const stop = (event: MouseEvent) => event.stopPropagation();

/**
 * Карточка неоплаченного счёта вверху дашборда партнёра. Вся область
 * содержимого это CardActionArea со ссылкой в «Биллинг», «Счета»; «Скачать
 * PDF» вынесена в CardActions под ней, чтобы ссылка не была вложена в
 * ссылку. Своего ховера у карточки нет, подсветка CardActionArea по
 * умолчанию накрывает всё содержимое.
 */
export const UnpaidInvoicesCard: FC<UnpaidInvoicesCardProps> = ({
  summary,
}) => {
  const { t } = useTranslation();
  const { nearest } = summary;
  const single = summary.count === 1;
  const overdue = summary.overdueDays > 0;

  return (
    <Card>
      <CardActionArea
        component={RouterLink}
        to={`${PagePaths.billing}?tab=invoices`}
      >
        <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            <ReceiptLongIcon color={overdue ? "error" : "warning"} />
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {single
                  ? t("billing.unpaidCard.titleOne")
                  : t("billing.unpaidCard.titleMany", { count: summary.count })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {single
                  ? t("billing.unpaidCard.toPay")
                  : t("billing.unpaidCard.total")}
              </Typography>
              <Typography variant="h5" component="p">
                {summary.totals
                  .map((amount) => formatAmountObject(amount))
                  .join(", ")}
              </Typography>
              {single && nearest.invoice && (
                <Typography variant="body2">
                  {t("billing.unpaidCard.number", {
                    number: nearest.invoice.number,
                  })}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {single
                  ? t("billing.unpaidCard.dueAt", { value: nearest.expiredAt })
                  : t("billing.unpaidCard.nearestDueAt", {
                      value: nearest.expiredAt,
                    })}
              </Typography>
              {(overdue || summary.others > 0) && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                >
                  {overdue && (
                    <Chip
                      size="small"
                      color="error"
                      label={t("billing.unpaidCard.overdueDays", {
                        count: summary.overdueDays,
                      })}
                    />
                  )}
                  {summary.others > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("billing.unpaidCard.more", {
                        count: summary.others,
                      })}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      {summary.downloadUrl && (
        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={summary.downloadUrl}
            onClick={stop}
          >
            {t("billing.unpaidCard.download")}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
